/**
 * License verification client (DESIGN_RECORD license model; D3).
 *
 * The credential is an email + key pair. The server (Block 6: a
 * Cloudflare Worker) checks the pair against the license record and
 * returns a short-lived signed pass; the app stores the pass and
 * remembers the pair in this browser only, as the design record
 * allows. Survey data is never part of this exchange — the verify
 * call is the ONLY network request the application makes (brief P1,
 * SEC-01).
 *
 * Block 4 ships the client against this typed interface with a local
 * stub implementation, so the app is complete and testable now.
 * Block 6 swaps the transport: `HttpLicenseVerifier` below is the
 * real implementation, selected when VITE_LICENSE_VERIFY_URL is set.
 */

export interface LicensePass {
  readonly email: string;
  /** The key as entered; remembered locally so returning users skip retyping. */
  readonly key: string;
  /** Opaque signed pass returned by the server. */
  readonly pass: string;
  readonly verifiedAt: string;
  /** The signed pass is short-lived; the license term is what unlocks. */
  readonly passExpiresAt: string;
  readonly licenseExpiresAt: string;
}

export type VerifyFailureReason =
  | 'invalid-pair'
  | 'revoked'
  | 'expired'
  | 'network'
  | 'unavailable';

export type VerifyOutcome =
  | { readonly ok: true; readonly pass: LicensePass }
  | {
      readonly ok: false;
      readonly reason: VerifyFailureReason;
      readonly message: string;
    };

export interface LicenseVerifier {
  verify(email: string, key: string): Promise<VerifyOutcome>;
}

const DAY_MS = 24 * 60 * 60 * 1000;
const YEAR_MS = 365 * DAY_MS;

/**
 * Local stub verifier for development and tests. It accepts exactly
 * the documented test pairs and nothing else; it performs no network
 * call. This is a stand-in for the Block 6 Worker, never a licensing
 * decision: production builds set VITE_LICENSE_VERIFY_URL and use
 * HttpLicenseVerifier.
 */
export class StubLicenseVerifier implements LicenseVerifier {
  readonly #pairs: Readonly<Record<string, string>>;

  constructor(pairs?: Readonly<Record<string, string>>) {
    this.#pairs = pairs ?? {
      // Documented development pair (see apps/web README).
      'analyst@example.com': 'SAE-STUDIO-TEST-KEY',
    };
  }

  async verify(email: string, key: string): Promise<VerifyOutcome> {
    const normalized = email.trim().toLowerCase();
    const expected = this.#pairs[normalized];
    if (expected === undefined || expected !== key.trim()) {
      return {
        ok: false,
        reason: 'invalid-pair',
        message:
          'That email and license key do not match a license on file. Check both entries and try again.',
      };
    }
    const now = Date.now();
    return {
      ok: true,
      pass: {
        email: normalized,
        key: key.trim(),
        pass: `stub-pass.${normalized}`,
        verifiedAt: new Date(now).toISOString(),
        passExpiresAt: new Date(now + DAY_MS).toISOString(),
        licenseExpiresAt: new Date(now + YEAR_MS).toISOString(),
      },
    };
  }
}

/**
 * The Block 6 transport: POST {email, key} to the verification
 * endpoint; 200 returns {pass, passExpiresAt, licenseExpiresAt}.
 * Status mapping: 401/403 invalid pair, 410 revoked, 402/409 expired,
 * anything else unavailable.
 */
export class HttpLicenseVerifier implements LicenseVerifier {
  readonly #url: string;
  readonly #fetchFn: typeof fetch;

  constructor(url: string, fetchFn: typeof fetch = fetch) {
    this.#url = url;
    // Chrome's window.fetch brand-checks its receiver: calling the
    // stored reference as a method of this class throws "Failed to
    // execute 'fetch' on 'Window': Illegal invocation" (found by the
    // 2026-10-17 stranger test; Node and jsdom fetches do not check).
    // Bind the default to the global scope; injected test doubles are
    // plain functions and are stored as given.
    this.#fetchFn =
      fetchFn === fetch ? fetch.bind(globalThis) : fetchFn;
  }

  async verify(email: string, key: string): Promise<VerifyOutcome> {
    const normalized = email.trim().toLowerCase();
    let response: Response;
    try {
      response = await this.#fetchFn(this.#url, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email: normalized, key: key.trim() }),
      });
    } catch {
      return {
        ok: false,
        reason: 'network',
        message:
          'The license check could not reach the verification service. Check your connection and try again.',
      };
    }
    if (response.status === 200) {
      const body = (await response.json()) as {
        pass: string;
        passExpiresAt: string;
        licenseExpiresAt: string;
      };
      return {
        ok: true,
        pass: {
          email: normalized,
          key: key.trim(),
          pass: body.pass,
          verifiedAt: new Date().toISOString(),
          passExpiresAt: body.passExpiresAt,
          licenseExpiresAt: body.licenseExpiresAt,
        },
      };
    }
    if (response.status === 401 || response.status === 403) {
      return {
        ok: false,
        reason: 'invalid-pair',
        message:
          'That email and license key do not match a license on file. Check both entries and try again.',
      };
    }
    if (response.status === 410) {
      return {
        ok: false,
        reason: 'revoked',
        message:
          'This license key has been revoked. If you bought a license, contact support; a replacement key will be issued to the email address on file.',
      };
    }
    if (response.status === 402 || response.status === 409) {
      return {
        ok: false,
        reason: 'expired',
        message:
          'This license has expired. Renew it on the product page to keep using the full tool.',
      };
    }
    return {
      ok: false,
      reason: 'unavailable',
      message:
        'The verification service is unavailable. Your work is saved in this browser. Try the check again in a few minutes.',
    };
  }
}

/**
 * Select the verifier for this build: the HTTP transport when the
 * Block 6 endpoint is configured, the local stub otherwise.
 */
export function createLicenseVerifier(url?: string): LicenseVerifier {
  const endpoint =
    url ??
    (typeof import.meta !== 'undefined'
      ? (import.meta.env?.VITE_LICENSE_VERIFY_URL as string | undefined)
      : undefined);
  return endpoint
    ? new HttpLicenseVerifier(endpoint)
    : new StubLicenseVerifier();
}
